import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { LoginForm, LoginFormTheme } from '@masonic-tools/shared-ui';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import Header from '@/components/layout/Header';
import { generateDeviceId } from '@/lib/totp';

const diningTheme: LoginFormTheme = {
  logo: '/logo.png',
  appName: 'Dining Steward',
  appNameColor: 'text-navy',
  welcomeTitle: 'Welcome Back',
  welcomeDescription: 'Log in to your Dining Steward account',
  signUpPath: '/signup',
  resetPasswordPath: '/reset-password',
  defaultReturnPath: '/dashboard',
};

export default function LoginPage() {
  const { login, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as any)?.returnTo || '/dashboard';

  const handleLogin = async (email: string, password: string) => {
    await login(email, password);
    
    // After successful login, check if user has 2FA enabled
    setTimeout(async () => {
      await check2FARequired();
    }, 500);
  };

  const check2FARequired = async () => {
    try {
      // Get the current user from auth
      const { auth } = await import('@/lib/firebase');
      const user = auth.currentUser;
      
      if (!user) {
        return;
      }

      // Fetch user profile from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        navigate(returnTo);
        return;
      }

      const userData = userDoc.data();
      
      // Check if 2FA is enabled
      if (!userData.twoFactorEnabled) {
        navigate(returnTo);
        return;
      }

      // Check if current device is trusted
      const deviceId = generateDeviceId();
      const trustedDevices = userData.trustedDevices || [];
      const isTrusted = trustedDevices.some((d: any) => d.id === deviceId);

      if (isTrusted) {
        // Update last used time for this device
        const updatedDevices = trustedDevices.map((d: any) => 
          d.id === deviceId ? { ...d, lastUsed: new Date() } : d
        );
        await updateDoc(doc(db, 'users', user.uid), {
          trustedDevices: updatedDevices,
        });
        
        navigate(returnTo);
      } else {
        // Device not trusted, redirect to 2FA verification page
        navigate('/verify-2fa', { state: { returnTo } });
      }
    } catch (error) {
      console.error('Error checking 2FA:', error);
      navigate(returnTo);
    }
  };

  return (
    <LoginForm
      theme={diningTheme}
      onLogin={handleLogin}
      currentUser={currentUser}
      header={<Header />}
    />
  );
}
